/**
 * Placement — step 3 of the pagination model (template spec §7).
 *
 * Deliberately PURE: it takes measurements in and returns a plan out, with no browser
 * and no DOM. Two reasons.
 *
 *   1. It can be tested at realistic scale. B-4 requires proving a 5,000-row venue
 *      table paginates correctly; doing that through Playwright for every case would
 *      be too slow to run in CI, so the algorithm is exercised directly and the
 *      browser integration is verified separately.
 *   2. The hard part is arithmetic, not rendering. Orphan control, widow pull-back and
 *      header accounting are where this goes wrong, and none of them need a browser to
 *      get wrong.
 *
 * The backlog calls this "the hardest block in the catalog — prototype early", and the
 * reason is that every rule interacts: reserving space for a repeated header changes
 * how many rows fit, which changes where the last fragment starts, which is what widow
 * control then has to fix up.
 */

/** A splittable run of rows inside a breakable block. */
export interface RowGroup {
  /** Repeating header height. 0 when the block declares no header. */
  headerHeight: number;
  /** Whether the header repeats on continuation fragments (manifest-driven). */
  repeatHeader: boolean;
  rowHeights: number[];
  /** Trailing caption — "showing N of M". Rendered once, on the final fragment. */
  footerHeight: number;
  /** Non-row chrome above the rows: card title bar, padding. */
  chromeHeight: number;
}

export interface MeasuredBlock {
  id: string;
  flow: 'atomic' | 'breakable';
  /** Height rendered whole, including its own chrome. */
  height: number;
  /** Vertical gap that follows this block. */
  marginBottom: number;
  /** Present only when the block actually has splittable rows. */
  rows?: RowGroup;
}

export type Fragment =
  | { blockId: string; kind: 'whole' }
  | {
      blockId: string;
      kind: 'rows';
      /** Inclusive start, exclusive end. */
      from: number;
      to: number;
      withHeader: boolean;
      /** The trailing caption renders only on the final fragment. */
      withFooter: boolean;
      /** True for the fragment that opens the block (carries its card chrome). */
      isFirst: boolean;
    };

export interface LaidOutPage {
  fragments: Fragment[];
  height: number;
}

export interface Overflow {
  blockId: string;
  /** How much taller than a whole page this element is. */
  excess: number;
  reason: 'atomic-taller-than-page' | 'row-taller-than-page';
}

export interface PaginateResult {
  pages: LaidOutPage[];
  /** Content that cannot fit a page at any placement. Never silently clipped. */
  overflows: Overflow[];
}

export interface PaginateOptions {
  /** Usable content height of one page, in the same units as the measurements. */
  pageHeight: number;
  /**
   * Never strand fewer than this many rows alone at the top or bottom of a page.
   *
   * Two is the meaningful minimum: a single row under a repeated header reads as a
   * rendering fault, and it is what "no orphaned rows" in B-4's acceptance criterion
   * is asking for.
   */
  minRowsPerFragment?: number;
}

const DEFAULT_MIN_ROWS = 2;

export function paginate(blocks: MeasuredBlock[], options: PaginateOptions): PaginateResult {
  const budget = options.pageHeight;
  const minRows = options.minRowsPerFragment ?? DEFAULT_MIN_ROWS;

  const pages: LaidOutPage[] = [{ fragments: [], height: 0 }];
  const overflows: Overflow[] = [];

  const current = () => pages[pages.length - 1];
  const remaining = () => budget - current().height;
  const newPage = () => { pages.push({ fragments: [], height: 0 }); };
  for (const block of blocks) {
    const whole = block.height + block.marginBottom;

    // Fits as-is. The trailing margin is capped for the same reason it is capped
    // after a split: a margin below the last element on a page collapses at the break.
    if (block.height <= remaining()) {
      current().fragments.push({ blockId: block.id, kind: 'whole' });
      current().height = Math.min(budget, current().height + whole);
      continue;
    }

    const splittable = block.flow === 'breakable' && block.rows && block.rows.rowHeights.length > 0;

    if (!splittable) {
      // Atomic (or breakable with nothing to split): push whole to the next page.
      if (block.height > budget) {
        // Taller than any page. Nothing to be done but place it and say so — clipping
        // it silently is how a report loses content without anyone noticing.
        overflows.push({
          blockId: block.id,
          excess: block.height - budget,
          reason: 'atomic-taller-than-page',
        });
      }
      if (current().fragments.length > 0) newPage();
      current().fragments.push({ blockId: block.id, kind: 'whole' });
      current().height = Math.min(budget, current().height + whole);
      continue;
    }

    splitBlock(block, block.rows!);
  }

  /**
   * Split one breakable block across pages.
   *
   * Header accounting is the subtle part: the opening fragment carries the block's
   * card chrome and its header, continuation fragments carry the header only when the
   * manifest says it repeats, and the trailing caption belongs to the last fragment
   * alone. Each of those changes the space available for rows on that page.
   */
  function splitBlock(block: MeasuredBlock, group: RowGroup) {
    const n = group.rowHeights.length;
    /** Per-fragment row spans, in placement order. */
    const spans: Array<{ from: number; to: number; isFirst: boolean }> = [];

    let i = 0;
    let isFirst = true;

    while (i < n) {
      const overhead =
        (isFirst ? group.chromeHeight : 0) +
        (isFirst || group.repeatHeader ? group.headerHeight : 0);

      const fit = (space: number) => {
        let count = 0;
        let height = 0;
        while (i + count < n && height + group.rowHeights[i + count] <= space) {
          height += group.rowHeights[i + count];
          count++;
        }
        return { count, height };
      };

      let { count: take, height: used } = fit(remaining() - overhead);

      /*
       * The trailing caption ("showing N of M") belongs to the final fragment, so its
       * height has to be reserved there — and only there. Reserving it on every
       * fragment would waste a caption's worth of space on every page of a 99-page
       * table; not reserving it at all overflows the last page, which is what this
       * originally did.
       *
       * If the caption does not fit alongside the remaining rows, deliberately hold
       * back `minRows` so the final page carries those rows AND the caption together.
       * Pushing the caption onto a page of its own would be an orphan of a different
       * kind.
       */
      if (group.footerHeight > 0 && i + take === n) {
        const withFooter = fit(remaining() - overhead - group.footerHeight);
        if (i + withFooter.count === n) {
          take = withFooter.count;
          used = withFooter.height;
        } else if (take > minRows) {
          take -= minRows;
          used = group.rowHeights.slice(i, i + take).reduce((a, b) => a + b, 0);
        }
      }

      const rowsLeftAfter = n - i - take;

      // Orphan control: refuse to open a fragment that would strand too few rows,
      // unless this is genuinely everything that is left.
      if (take < minRows && rowsLeftAfter > 0) {
        if (current().fragments.length === 0) {
          // Already at the top of an empty page and still cannot fit minRows. Either
          // a single row is taller than a page, or the overhead consumes it.
          if (take === 0) {
            const tallest = group.rowHeights[i];
            if (tallest + overhead > budget) {
              overflows.push({
                blockId: block.id,
                excess: tallest + overhead - budget,
                reason: 'row-taller-than-page',
              });
              take = 1; // place it anyway rather than loop forever
              used = tallest;
            } else {
              take = 1;
              used = tallest;
            }
          }
          // else: fewer than minRows fit on an empty page; accept what fits.
        } else {
          newPage();
          isFirst = isFirst && spans.length === 0;
          continue;
        }
      }

      if (take === 0) {
        // Nothing fits in the remaining space; start a fresh page and retry.
        newPage();
        continue;
      }

      spans.push({ from: i, to: i + take, isFirst });
      const isFinalFragment = i + take === n;
      current().height += overhead + used + (isFinalFragment ? group.footerHeight : 0);
      // Remember which page each span landed on by placing a marker we rewrite below.
      current().fragments.push({
        blockId: block.id, kind: 'rows',
        from: i, to: i + take, withHeader: isFirst || group.repeatHeader,
        withFooter: false, isFirst,
      });

      i += take;
      isFirst = false;
      if (i < n) newPage();
    }

    // Widow control: if the final fragment ended up with too few rows, pull rows back
    // from the fragment before it. A two-page table whose second page holds one row is
    // the most visible pagination defect there is.
    const emitted = pages.flatMap((p) => p.fragments).filter(
      (f): f is Extract<Fragment, { kind: 'rows' }> => f.kind === 'rows' && f.blockId === block.id
    );
    if (emitted.length >= 2) {
      const last = emitted[emitted.length - 1];
      const prev = emitted[emitted.length - 2];
      const lastCount = last.to - last.from;
      const prevCount = prev.to - prev.from;
      if (lastCount < minRows && prevCount - (minRows - lastCount) >= minRows) {
        const move = minRows - lastCount;
        // Only worth doing if the receiving page can take the extra height.
        const lastPage = pages.find((p) => p.fragments.includes(last))!;
        const extra = group.rowHeights.slice(last.from - move, last.from).reduce((a, b) => a + b, 0);
        if (lastPage.height + extra <= budget) {
          prev.to -= move;
          last.from -= move;
          const prevPage = pages.find((p) => p.fragments.includes(prev))!;
          prevPage.height -= extra;
          lastPage.height += extra;
        }
      }
      emitted[emitted.length - 1].withFooter = true;
    } else if (emitted.length === 1) {
      emitted[0].withFooter = true;
    }

    /*
     * The block's trailing margin, capped at the page.
     *
     * A margin below the last element on a page collapses against the page break in
     * CSS — it does not push content onto another sheet, and it must not push the
     * accounted height past the budget either. Adding it unconditionally is what made
     * a table whose rows exactly filled a page report 912 of a 900px budget.
     */
    current().height = Math.min(budget, current().height + block.marginBottom);
  }

  // A trailing empty page can be produced when the last block exactly filled the one
  // before it. It is not a blank sheet anyone asked for.
  while (pages.length > 1 && pages[pages.length - 1].fragments.length === 0) pages.pop();

  return { pages, overflows };
}

/**
 * Consistency check over a plan.
 *
 * Used by the acceptance suite and cheap enough to run in production: a plan that
 * loses or duplicates a row is a defect that silently drops content from a client
 * report, which is precisely the failure this system exists to prevent.
 */
export function verifyPlan(blocks: MeasuredBlock[], result: PaginateResult, pageHeight: number): string[] {
  const issues: string[] = [];

  for (const block of blocks) {
    const frags = result.pages.flatMap((p) => p.fragments).filter((f) => f.blockId === block.id);
    if (frags.length === 0) { issues.push(`${block.id}: never placed`); continue; }

    const rowFrags = frags.filter((f): f is Extract<Fragment, { kind: 'rows' }> => f.kind === 'rows');
    if (rowFrags.length === 0) continue;

    // Every row exactly once, in order.
    const covered: number[] = [];
    for (const f of rowFrags) for (let r = f.from; r < f.to; r++) covered.push(r);
    const expected = block.rows!.rowHeights.length;
    if (covered.length !== expected) {
      issues.push(`${block.id}: ${covered.length} rows placed, expected ${expected}`);
    }
    for (let r = 0; r < expected; r++) {
      if (covered[r] !== r) { issues.push(`${block.id}: rows out of order or duplicated at ${r}`); break; }
    }
    const footers = rowFrags.filter((f) => f.withFooter).length;
    if (footers !== 1) issues.push(`${block.id}: ${footers} trailing captions, expected exactly 1`);
    const firsts = rowFrags.filter((f) => f.isFirst).length;
    if (firsts !== 1) issues.push(`${block.id}: ${firsts} opening fragments, expected exactly 1`);
  }

  result.pages.forEach((p, i) => {
    // Allow the known overflow cases; anything else exceeding the budget is a bug.
    const hasOverflow = p.fragments.some((f) => result.overflows.some((o) => o.blockId === f.blockId));
    if (p.height > pageHeight + 0.5 && !hasOverflow) {
      issues.push(`page ${i + 1}: height ${p.height.toFixed(1)} exceeds budget ${pageHeight}`);
    }
    if (p.fragments.length === 0) issues.push(`page ${i + 1}: empty`);
  });

  return issues;
}
